import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMobileUiV1State } from "../../hooks/use-media-query";
import { navigationPageTitle } from "../../lib/navigation";
import type { AuthUser } from "../../stores/auth";
import { DesktopHeader } from "./desktop-header";
import { MobileAppBar } from "./mobile-app-bar";
import { MobileNavigationSheet } from "./mobile-navigation-sheet";
import { DesktopOnlyNotice } from "../ui/desktop-only-notice";

const BELOW_MINIMUM_ALLOWED_PATHS = new Set(["/login", "/change-password"]);
type OpenMobileSheetSection = "navigation" | "account" | null;

interface AppShellProps {
  user: AuthUser | null;
  pathname: string;
  hideHeaderIdentity: boolean;
  identityPreferenceBusy: boolean;
  onToggleIdentity(): void;
  onLogout(): void;
  children: ReactNode;
}

export function AppShell({
  user,
  pathname,
  hideHeaderIdentity,
  identityPreferenceBusy,
  onToggleIdentity,
  onLogout,
  children,
}: AppShellProps) {
  const mobileUi = useMobileUiV1State();
  const [openMobileSheetSection, setOpenMobileSheetSection] =
    useState<OpenMobileSheetSection>(null);
  const navigationButtonRef = useRef<HTMLButtonElement>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const useMobileShell = mobileUi.presenter !== "desktop";
  const showMinimumWidthBoundary =
    mobileUi.isBelowMinimumWidth &&
    !BELOW_MINIMUM_ALLOWED_PATHS.has(pathname);

  useEffect(() => {
    setOpenMobileSheetSection(null);
  }, [pathname]);

  useEffect(() => {
    if (!useMobileShell) setOpenMobileSheetSection(null);
  }, [useMobileShell]);

  const handleMobileSheetOpenChange = (open: boolean) => {
    setOpenMobileSheetSection((current) =>
      open ? current ?? "navigation" : null
    );
  };

  return (
    <div className="app-shell flex min-h-screen flex-col">
      {useMobileShell ? (
        <MobileAppBar
          user={user}
          pageTitle={navigationPageTitle(pathname)}
          navigationOpen={openMobileSheetSection === "navigation"}
          accountOpen={openMobileSheetSection === "account"}
          onOpenNavigation={() => setOpenMobileSheetSection("navigation")}
          onOpenAccount={() => setOpenMobileSheetSection("account")}
          navigationButtonRef={navigationButtonRef}
          accountButtonRef={accountButtonRef}
        />
      ) : (
        <DesktopHeader
          user={user}
          pathname={pathname}
          hideHeaderIdentity={hideHeaderIdentity}
          identityPreferenceBusy={identityPreferenceBusy}
          onToggleIdentity={onToggleIdentity}
          onLogout={onLogout}
        />
      )}

      <main className="min-w-0 flex-1">
        {showMinimumWidthBoundary ? (
          <div className="mx-auto max-w-lg px-4 py-8">
            <DesktopOnlyNotice minimumWidth={360} />
          </div>
        ) : (
          children
        )}
      </main>

      {useMobileShell && user && (
        <MobileNavigationSheet
          open={openMobileSheetSection !== null}
          onOpenChange={handleMobileSheetOpenChange}
          initialSection={openMobileSheetSection ?? "navigation"}
          user={user}
          pathname={pathname}
          hideHeaderIdentity={hideHeaderIdentity}
          identityPreferenceBusy={identityPreferenceBusy}
          onToggleIdentity={onToggleIdentity}
          onLogout={onLogout}
          restoreFocusRef={
            openMobileSheetSection === "account"
              ? accountButtonRef
              : navigationButtonRef
          }
        />
      )}
    </div>
  );
}
